import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaLock, FaUser, FaStar, FaChartBar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { formsService } from '../../../../services/formsService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { useCurrentForm } from '../../../../hooks/community/useForms';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import Avatar from '../../../../components/misc/Avatar';
import { FormQuestionType, FormResponse } from '../../../../models/forms.model';
import Button from '../../../../components/misc/Button';

export default function CommunityFormResultsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { formSlug } = useParams<{ formSlug: string }>();
  const community = useSelectedCommunity();
  const currentForm = useCurrentForm();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!community?.id || !formSlug) return;

    setLoading(true);
    formsService
      .getFormBySlug(community.id, formSlug)
      .catch((error) => {
        console.error('Failed to load form:', error);
        toast.error(t('error.loadFailed'));
        navigate('/community/forms');
      })
      .finally(() => setLoading(false));
  }, [community?.id, formSlug]);

  const handleBack = () => {
    navigate(`/community/forms/${formSlug}`);
  };

  // Aggregate responses by question for summary (choice, rating, number, date, text)
  const questionSummaries = useMemo(() => {
    if (!currentForm) return {};

    const summaries: Record<number, any> = {};

    // Initialize summaries per question
    currentForm.questions.forEach((question) => {
      summaries[question.formQuestionId] = { type: question.type, data: {} };

      switch (question.type) {
        case FormQuestionType.CHOICE:
          const optionCounts: Record<number, number> = {};
          question.options?.forEach((opt) => {
            optionCounts[opt.formOptionId] = 0;
          });
          summaries[question.formQuestionId].data = optionCounts;
          break;

        case FormQuestionType.RATING:
          const scale = question.meta?.scale || 5;
          const counts: Record<number, number> = {};
          for (let i = 1; i <= scale; i += 1) counts[i] = 0;
          summaries[question.formQuestionId].data = { counts, total: 0, sum: 0, scale };
          break;

        case FormQuestionType.NUMBER:
          summaries[question.formQuestionId].data = { values: [] as number[] };
          break;

        case FormQuestionType.DATE:
          summaries[question.formQuestionId].data = { dates: [] as string[] };
          break;

        case FormQuestionType.TEXT:
        default:
          summaries[question.formQuestionId].data = { freq: {} as Record<string, number> };
      }
    });

    // Aggregate answers
    currentForm.responses.forEach((response) => {
      response.answers.forEach((answer) => {
        const questionObj = currentForm.questions.find((qq) => qq.formQuestionId === answer.questionId);
        if (!questionObj) return;
        const s = summaries[answer.questionId];
        if (!s) return;

        switch (questionObj.type) {
          case FormQuestionType.CHOICE:
            if (answer.optionId) {
              s.data[answer.optionId] = (s.data[answer.optionId] || 0) + 1;
            }
            break;

          case FormQuestionType.RATING:
            const rating = Number(answer.text || 0);
            if (rating) {
              s.data.counts[rating] = (s.data.counts[rating] || 0) + 1;
              s.data.total = (s.data.total || 0) + 1;
              s.data.sum = (s.data.sum || 0) + rating;
            }
            break;

          case FormQuestionType.NUMBER:
            const num = Number(answer.text);
            if (!Number.isNaN(num)) s.data.values.push(num);
            break;

          case FormQuestionType.DATE:
            if (answer.text) s.data.dates.push(answer.text);
            break;

          case FormQuestionType.TEXT:
          default:
            const txt = (answer.text || '').trim();
            if (txt) {
              s.data.freq[txt] = (s.data.freq[txt] || 0) + 1;
            }
        }
      });
    });

    // Post-process derived stats
    Object.keys(summaries).forEach((key) => {
      const qid = Number(key);
      const s = summaries[qid];

      if (s.type === FormQuestionType.RATING) {
        const total = s.data.total || 0;
        s.data.average = total > 0 ? s.data.sum / total : 0;
      } else if (s.type === FormQuestionType.NUMBER) {
        const v = s.data.values as number[];
        s.data.count = v.length;
        s.data.average = v.length > 0 ? v.reduce((a, b) => a + b, 0) / v.length : 0;
        s.data.min = v.length > 0 ? Math.min(...v) : null;
        s.data.max = v.length > 0 ? Math.max(...v) : null;
      } else if (s.type === FormQuestionType.DATE) {
        const dates = s.data.dates as string[];
        if (dates.length > 0) {
          const sorted = dates.map((d) => new Date(d)).sort((a, b) => a.getTime() - b.getTime());
          s.data.earliest = sorted[0].toISOString();
          s.data.latest = sorted[sorted.length - 1].toISOString();
        } else {
          s.data.earliest = null;
          s.data.latest = null;
        }
      } else if (s.type === FormQuestionType.TEXT) {
        const freq = s.data.freq as Record<string, number>;
        s.data.top = Object.entries(freq)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([text, count]) => ({ text, count }));
      }
    });

    return summaries;
  }, [currentForm]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAnswerDisplay = (response: FormResponse, questionId: number) => {
    const answers = response.answers.filter((a) => a.questionId === questionId);
    const question = currentForm?.questions.find((q) => q.formQuestionId === questionId);

    if (!question || answers.length === 0) return '-';

    switch (question.type) {
      case FormQuestionType.CHOICE:
        const selectedOptions = answers
          .map((a) => question.options?.find((o) => o.formOptionId === a.optionId)?.label)
          .filter(Boolean);
        return selectedOptions.join(', ') || '-';

      case FormQuestionType.RATING:
        const rating = Number(answers[0]?.text || 0);
        const scale = question.meta?.scale || 5;
        return (
          <div className="flex items-center gap-1">
            {Array.from({ length: scale }, (_, i) => (
              <FaStar key={i} className={`h-4 w-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} />
            ))}
            <span className="ml-2 text-sm text-gray-600">
              ({rating}/{scale})
            </span>
          </div>
        );

      case FormQuestionType.DATE:
        const dateValue = answers[0]?.text;
        return dateValue
          ? new Date(dateValue).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '-';

      default:
        return answers[0]?.text || '-';
    }
  };

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-details">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-details">{t('general.loading')}</p>
      </div>
    );
  }

  if (!currentForm) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <p className="text-details">{t('page.forms.notFound')}</p>
        <button
          type="button"
          onClick={handleBack}
          className="rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-opacity-90">
          {t('general.back')}
        </button>
      </div>
    );
  }

  // Check if results are private
  if (currentForm.responsesHidden) {
    return (
      <div className="flex flex-col gap-4">
        <CommunityHeader
          title={t('page.forms.results.title', { title: currentForm.title })}
          action={
            <button
              type="button"
              onClick={handleBack}
              className="flex items-center gap-2 rounded-lg border border-primary px-4 py-2 text-primary transition-colors hover:bg-primary/5">
              <FaArrowLeft className="h-4 w-4" />
              {t('page.forms.backToForm')}
            </button>
          }
        />

        <div className="flex flex-col items-center justify-center gap-4 rounded-xl border bg-white p-12">
          <FaLock className="h-12 w-12 text-details" />
          <h2 className="text-xl font-semibold text-primary">{t('page.forms.results.private')}</h2>
          <p className="text-center text-details">{t('page.forms.results.privateDescription')}</p>
        </div>
      </div>
    );
  }

  const totalResponses = currentForm.responses.length;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Header */}
      <div className="flex w-full items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/community/forms')}
          className="flex items-center gap-2 text-gray-600 hover:text-primary">
          <FaArrowLeft className="h-4 w-4" />
          {t('page.forms.backToForms')}
        </button>

        <Button variant="primary" onClick={() => navigate(`/community/forms/${formSlug}`)}>
          <FaChartBar className="h-4 w-4" />
          {t('page.forms.viewSubmission')}
        </Button>
      </div>

      <div className="flex w-full max-w-4xl flex-col gap-4">
        {/* Form Title & Description */}
        <div className="rounded-xl border border-secondary-200">
          <div className="flex rounded-t-xl bg-secondary-200 p-2">
            <h1 className="text-2xl font-bold text-primary">{currentForm.title}</h1>
          </div>
          <div className="p-2">
            {currentForm.description && <p className="mt-2 text-gray-600">{currentForm.description}</p>}
            {currentForm.deadline && (
              <p className="mt-2 text-sm text-gray-500">
                {t('page.forms.deadline')}:{' '}
                {new Date(currentForm.deadline).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 p-2 text-details">
            <FaChartBar className="h-5 w-5" />
            <p>{t('page.forms.results.totalResponses', { count: totalResponses })}</p>
          </div>
        </div>
      </div>

      <div className="flex w-full max-w-4xl flex-col gap-4">
        {totalResponses === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-secondary-200 bg-secondary-50 p-12">
            <FaUser className="h-12 w-12 text-details" />
            <p className="text-details">{t('page.forms.results.noResponses')}</p>
          </div>
        ) : (
          <>
            {/* Summary for All Questions */}
            <div className="flex flex-col gap-4 p-2">
              {currentForm.questions.map((question, index) => {
                const s = questionSummaries[question.formQuestionId];
                return (
                  <div key={question.formQuestionId} className="rounded-lg border border-secondary-200">
                    <div className="mb-3 flex items-start gap-2 rounded-t-lg bg-secondary-200 p-2">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{question.label}</p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 p-2">
                      {/* Choice */}
                      {question.type === FormQuestionType.CHOICE &&
                        question.options?.map((option) => {
                          const count = s?.data?.[option.formOptionId] || 0;
                          const percentage = totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0;

                          return (
                            <div key={option.formOptionId} className="flex items-center gap-3">
                              <div className="flex-1">
                                <div className="flex justify-between text-sm">
                                  <span className="text-primary">{option.label}</span>
                                  <span className="text-details">
                                    {count} ({percentage}%)
                                  </span>
                                </div>
                                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary-200">
                                  <div
                                    className="h-full rounded-full bg-primary transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      {/* Rating */}
                      {question.type === FormQuestionType.RATING && (
                        <div>
                          <div className="mb-2 text-sm text-details">
                            {t('page.forms.results.average')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.average ? s.data.average.toFixed(1) : '-'}
                            </strong>
                          </div>
                          <div className="flex flex-col gap-1">
                            {Array.from({ length: s?.data?.scale || 5 }, (_, i) => {
                              const value = i + 1;
                              const count = s?.data?.counts?.[value] || 0;
                              const percentage = s?.data?.total ? Math.round((count / s.data.total) * 100) : 0;
                              return (
                                <div key={value} className="flex items-center gap-3">
                                  <div className="w-12 text-sm text-primary">{value}★</div>
                                  <div className="flex-1">
                                    <div className="flex justify-between text-sm">
                                      <span />
                                      <span className="text-details">
                                        {count} ({percentage}%)
                                      </span>
                                    </div>
                                    <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-secondary-200">
                                      <div
                                        className="h-full rounded-full bg-primary"
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Number */}
                      {question.type === FormQuestionType.NUMBER && (
                        <div className="text-sm text-details">
                          <div>
                            {t('page.forms.results.average')}:{' '}
                            <strong className="text-primary">{s?.data?.average?.toFixed(2) ?? '-'}</strong>
                          </div>
                          <div>
                            {t('page.forms.results.min')}:{' '}
                            <strong className="text-primary">{s?.data?.min ?? '-'}</strong>{' '}
                            {t('page.forms.results.max')}:{' '}
                            <strong className="text-primary">{s?.data?.max ?? '-'}</strong>
                          </div>
                        </div>
                      )}

                      {/* Date */}
                      {question.type === FormQuestionType.DATE && (
                        <div className="text-sm text-details">
                          <div>
                            {t('page.forms.results.earliest')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.earliest ? formatDate(s.data.earliest) : '-'}
                            </strong>
                          </div>
                          <div>
                            {t('page.forms.results.latest')}:{' '}
                            <strong className="text-primary">
                              {s?.data?.latest ? formatDate(s.data.latest) : '-'}
                            </strong>
                          </div>
                        </div>
                      )}

                      {/* Text */}
                      {question.type === FormQuestionType.TEXT && (
                        <div className="text-sm text-details">
                          {s?.data?.top?.length ? (
                            <ul className="list-disc pl-5">
                              {s.data.top.map((tItem: any) => (
                                <li key={tItem.text} className="text-primary">
                                  {tItem.text} <span className="text-details">({tItem.count})</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Individual Responses */}
            <div className="rounded-xl border border-secondary-200">
              <div className="flex rounded-t-xl bg-secondary-200 p-2">
                <h2 className="text-lg font-semibold text-primary">{t('page.forms.results.individualResponses')}</h2>
              </div>
              <div className="flex flex-col gap-4 p-2">
                {currentForm.responses.map((response) => (
                  <div
                    key={response.formResponseId}
                    className="rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                    <div className="mb-3 flex items-center gap-3">
                      <Avatar
                        src={response.user.picture || ''}
                        alias={`${response.user.firstName} ${response.user.lastName}`}
                        className="h-10 w-10"
                      />
                      <div>
                        <p className="font-medium text-primary">
                          {response.user.firstName} {response.user.lastName}
                        </p>
                        <p className="text-xs text-details">{formatDate(response.createdAt)}</p>
                      </div>
                    </div>
                    <div className="ml-[52px] flex flex-col gap-2">
                      {currentForm.questions.map((question) => (
                        <div key={question.formQuestionId} className="text-sm">
                          <span className="font-medium text-primary">{question.label}:</span>{' '}
                          <span className="text-details">{getAnswerDisplay(response, question.formQuestionId)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
